/* eslint-disable @typescript-eslint/ban-ts-comment */
import { PlatformAuthorizer, PlatformClient } from '@quickplay/platform-authorizer';
import { CastMessage, MessageName, MessageOrigin, MessageType } from '../CastMessage';
import { CastManager } from '../CastManager';
import { UserContext } from '../UserContext';
import {
  createLogger,
  Logger,
  Error,
  isError,
  createEventEmitter,
  EventEmitter,
  eventObject,
  AnyFunc,
  createError,
} from '@quickplay/foundation';
import { CastPlayer } from '../CastPlayer';
import { createCastPlayerImpl } from './CastPlayerImpl';

export function createCastManagerImpl(): CastManager {
  //@ts-ignore
  const castContextInstance = isCastDefined() && cast.framework.CastContext.getInstance();
  const eventEmitter: EventEmitter = createEventEmitter();
  let castSession: any;
  let platformAuth: PlatformAuthorizer;
  let platformClientDevice: PlatformClient;
  let proxyClientDevice: PlatformClient;
  let channel: string;
  let castMessage: CastMessage;

  const logger: Logger = createLogger();
  const castManager: CastManager = {
    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      eventEmitter.unsubscribe(eventName, callback);
    },

    get castContext(): any {
      return castContextInstance;
    },
    get castSessionManager(): any {
      return castSession;
    },
    get castPlayer(): CastPlayer {
      return createCastPlayerImpl(this);
    },
    // API to initialise castManager
    initialize(
      castOptions: any,
      clientDevice: PlatformClient,
      platformAuthorizer: PlatformAuthorizer,
      channelNamespace: string,
    ) {
      if (!isCastDefined()) {
        logger.debug(
          'cast framework is not available. Ensure the cast framework is loaded properly and running on chrome browser.',
        );
        return;
      }

      try {
        //@ts-ignore
        castOptions.autoJoinPolicy = chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED;
        //@ts-ignore
        cast.framework.CastContext.getInstance().setOptions(castOptions);
        platformAuth = platformAuthorizer;
        platformClientDevice = clientDevice;
        channel = channelNamespace;
        castPlayerListeners();
      } catch (error) {
        logger.debug('cast session is failed to initialised', error as Error);
      }
    },
    // API to send user context or user device information to receiver right after initialization.
    async sendUserDeviceInformation() {
      if (castSession != null) {
        proxyClientDevice = {
          id: castSession.getCastDevice().label,
          name: 'chromecast',
        };
        try {
          const userAuthToken = await this.getUserAuthToken();
          const message: UserContext = {
            userAuthToken: userAuthToken,
            platformClient: platformClientDevice,
            proxyPlatformClient: proxyClientDevice,
          };
          castMessage = {
            messageName: MessageName.USER_CONTEXT,
            messageType: MessageType.INFO,
            messageOrigin: MessageOrigin.SENDER,
            message: message,
          };
          this.sendMessage(JSON.stringify(castMessage));
        } catch (error) {
          logger.debug('Send UserDeviceInformation failed', error as Error);
          throw error;
        }
      } else {
        logger.debug('cast session is not initialised');
      }
    },
    // API to send message to receiver via custom channel
    async sendMessage(payload: string): Promise<void | Error> {
      if (!castSession) {
        logger.warn('cast session is not initialised');
        return;
      }
      try {
        await castSession.sendMessage(channel, payload);
      } catch (error) {
        logger.error('Send message to receiver failed with error', error as Error);
        throw error;
      }
    },

    // API to get userAuthToken
    async getUserAuthToken(): Promise<string> {
      if (!platformAuth) {
        throw new Error('PlatformAuthorizer is not configured');
      }
      const userAuthToken = await platformAuth.userAuthorizationDelegate.fetchUserAuthorizationToken();
      if (isError(userAuthToken)) {
        logger.error('UserAuthorization token fetch error ', userAuthToken);
        throw userAuthToken;
      }
      return userAuthToken.accessToken;
    },

    // API to get channelNamespace
    getChannelNamespace(): string {
      if (!channel) {
        throw new Error('ChannelNamespace is not configured');
      }
      return channel;
    },

    destroy(stopCasting?: boolean): void {
      if (castContextInstance) {
        castContextInstance.endCurrentSession(stopCasting);
      }
      clearCastPlayerListeners();
    },
  };

  function isCastDefined(): boolean {
    //@ts-ignore
    return typeof cast !== 'undefined';
  }
  const createFLError = (errorJson: any, internalError?: Error): Error | undefined => {
    if (!errorJson || !errorJson.errorCode) {
      return undefined;
    }
    return createError(errorJson.errorCode, errorJson.errorMessage, internalError, errorJson.contextDescription);
  };

  const customMessageHandler = (_channelName: any, customEvent: any): void => {
    const castEvent = JSON.parse(customEvent);
    if (castEvent.messageName === MessageName.CUSTOM && castEvent.messageType.toLowerCase() === 'error') {
      const errorJson = castEvent.message;
      const internalError = createFLError(errorJson.internalError);
      const error = createFLError(errorJson, internalError);
      eventEmitter.publish(castEvent.messageType.toLowerCase(), error);
    }
  };

  const sessionStateHandler = (event: any): void => {
    switch (event.sessionState) {
      //@ts-ignore
      case cast.framework.SessionState.SESSION_STARTED:
      //@ts-ignore
      case cast.framework.SessionState.SESSION_RESUMED:
        logger.debug('Cast Session started.');
        //@ts-ignore
        castSession = cast.framework.CastContext.getInstance().getCurrentSession();
        castManager.castSessionManager.addMessageListener(castManager.getChannelNamespace(), customMessageHandler);
        castManager.sendUserDeviceInformation();
        eventEmitter.publish('castSessionConnected');
        break;
      //@ts-ignore
      case cast.framework.SessionState.SESSION_START_FAILED:
        logger.error('Failed to start session');
        eventEmitter.publish('castSessionFailed');
        break;
      //@ts-ignore
      case cast.framework.SessionState.SESSION_ENDED:
        castManager.castSessionManager.removeMessageListener(castManager.getChannelNamespace(), customMessageHandler);
        logger.debug('Cast Session ended.');
        eventEmitter.publish('castSessionEnded');
        break;
    }
  };

  function castPlayerListeners(): void {
    // Castcontext listeners
    if (!isCastDefined()) {
      return;
    }
    castContextInstance.addEventListener(
      //@ts-ignore
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      sessionStateHandler,
    );
  }

  function clearCastPlayerListeners(): void {
    // Castcontext listeners
    if (!isCastDefined()) {
      return;
    }
    castContextInstance.removeEventListener(
      //@ts-ignore
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      sessionStateHandler,
    );
  }

  return castManager;
}
